import { Injectable, Signal, inject, OnDestroy } from '@angular/core';
import { BaseNotifier, StateType } from '../notifiers/base-notifier';
import { Ref } from './ref';
import { SessionCleanupService } from '../services/session-cleanup-service';

/**
 * BaseProvider — clase base de todos los providers de la arquitectura.
 *
 * ── Reglas de scope ──────────────────────────────────────────────────────
 *
 *  • `@Injectable({ providedIn: 'root' })` → singleton para toda la app.
 *    Usar SOLO para providers que deben sobrevivir entre rutas:
 *    AuthProvider, SessionTimeoutProvider, DarkModeProvider…
 *
 *  • `@Injectable()` (sin providedIn) + listado en `providers[]` de un
 *    componente o ruta → scoped al scope de ese componente.
 *    Usar para TODOS los providers del viewer (TOC, mapa, capas, análisis…).
 *    Angular llama ngOnDestroy() al desmontar el componente → el notifier
 *    se destruye, los effects/handles de ArcGIS se liberan y el estado
 *    arranca limpio en cada nueva sesión.
 *
 * ── Por qué @Injectable() en la clase abstracta ──────────────────────────
 *
 *  @Injectable() sin providedIn en una clase abstracta NO registra nada en
 *  el injector raíz. Solo habilita el injection context para que `inject()`
 *  funcione dentro del constructor de esta clase base. Cada subclase
 *  concreta define su propio scope con su propio @Injectable(...).
 */
@Injectable()
export abstract class BaseProvider<N extends BaseNotifier<S>, S extends StateType> implements OnDestroy {
  protected readonly _notifier: N;
  protected readonly ref = inject(Ref);

  /** Funciones de baja del SessionCleanupService — ejecutadas en ngOnDestroy. */
  private readonly _sessionUnregisters: Array<() => void> = [];

  constructor(
    NotifierClass: new (ref: Ref, injector?: any) => N,
    injector?: any
  ) {
    this._notifier = new NotifierClass(this.ref, injector);
  }

  /** Estado (readonly signal). */
  get state(): Signal<S> {
    return this._notifier.state;
  }

  /** Notifier para métodos de mutación. */
  get notifier(): N {
    return this._notifier;
  }

  /**
   * Registra un callback de reset en el `SessionCleanupService` y garantiza
   * que se dará de baja automáticamente cuando Angular destruya el provider
   * (ngOnDestroy). Elimina completamente el boilerplate de `unregisterReset`.
   *
   * Uso en subclases:
   * ```ts
   * constructor() {
   *   super(MyNotifier);
   *   this.trackSessionReset(() => this.notifier.resetState());
   * }
   * ```
   *
   * ⚠️ Solo para providers scoped a ruta. En providers root (AuthProvider…)
   * el reset se gestiona explícitamente en `logout()`.
   */
  protected trackSessionReset(reset: () => void): void {
    const unregister = inject(SessionCleanupService).registerReset(reset);
    this._sessionUnregisters.push(unregister);
  }

  /**
   * Limpieza automática al destruir el provider (salida de la ruta / cierre
   * de sesión). Orden de limpieza:
   *   1. Se dan de baja todos los resets registrados en SessionCleanupService.
   *   2. Se llama dispose() del notifier (cleanups + onDispose()).
   *   3. Se invoca el hook onDestroy() de la subclase (si hay lógica extra).
   *
   * Las subclases NO deben redefinir ngOnDestroy — usan `protected override onDestroy()`.
   */
  ngOnDestroy(): void {
    // 1. Dar de baja los resets del SessionCleanupService.
    for (const unregister of this._sessionUnregisters) {
      try { unregister(); } catch { /* un error no bloquea el resto */ }
    }
    this._sessionUnregisters.length = 0;

    // 2. Destruir el notifier (effects, handles ArcGIS, timers).
    this._notifier.dispose();

    // 3. Hook de subclase para lógica extra.
    this.onDestroy();
  }

  /**
   * Hook de subclase para limpieza adicional al destruir el provider.
   * Solo necesario cuando hay recursos fuera del notifier que liberar.
   */
  protected onDestroy(): void {
    // Override en subclases si hace falta.
  }
}
